import { type WorkflowTemplate } from '@/api/workflows';

import { isOptionOffered, type OfferLanguages } from './clientLanguage';
import { type FieldValue } from './FieldRenderer';

/** Choice fields whose answers must be one of the field's own authored options. */
const CHOICE_TYPES = new Set(['SingleSelect', 'RadioGroup', 'CheckboxGroup']);

/**
 * Drops choice answers the form no longer offers, when an in-progress application
 * is reopened on a newer version of its workflow.
 *
 * ## Why only choices
 *
 * Answers are keyed by field id, and ids survive between versions, so most answers
 * carry over as they are — that is the "no loss of data where fields remain
 * compatible" the story asks for. Two things can change underneath them:
 *
 *   - a REMOVED field needs nothing here. Every save rebuilds the snapshot from the
 *     form being shown (`buildApplicationSnapshot`), so its answer drops out of the
 *     next save and no PDF mapping can print it.
 *   - a REMOVED OPTION is the one that bites. The old value still counts as an
 *     answer — the required check passes, the rail ticks the step, the resume walks
 *     past it — while matching nothing the form or the PDF knows. A required
 *     question would stay effectively unanswered with nothing prompting the advisor.
 *
 * Clearing it makes the question read as unanswered, and the resume position
 * (`resumePosition.ts`) then opens the application on exactly that step.
 *
 * ## What is left alone
 *
 * - Keys that match no field. Repeated sections store answers under
 *   `inst:<sectionId>:<n>:<fieldId>` and their instance counts under sentinels;
 *   pruning unknown keys would destroy those.
 * - The rep-code picker (`advisorRepCodeSelector`). Its options come from the
 *   advisor's session, not the form, and have their own reconciliation
 *   (`advisorPrefill.ts`).
 * - A choice field with no authored options — nothing to judge against.
 *
 * ## Language scope (`options.language`)
 *
 * The same rule, applied to an option the CLIENT's language does not offer (QA D-30 — the
 * French title list has no "Ms."). Passing a language narrows each field's allowed set to the
 * options offered in it, and narrows the walk to fields that actually scope an option, so
 * FormShell can run it on every change without touching anything else: an answer picked in
 * English is cleared the moment the client's language flips to French under it, and never
 * reaches the review or a PDF.
 *
 * Returns the SAME `values` object when nothing was cleared, so a caller inside a state updater
 * can tell "no change" apart from "changed" by identity.
 */
export function pruneStaleChoices(
  template: WorkflowTemplate,
  values: Record<string, FieldValue>,
  options: { language?: OfferLanguages } = {},
): { values: Record<string, FieldValue>; clearedFieldIds: string[] } {
  const { language } = options;
  const next: Record<string, FieldValue> = { ...values };
  const cleared = new Set<string>();

  for (const step of template.steps) {
    if (step.type !== 'DynamicForm') continue;
    for (const section of step.sections ?? []) {
      const instancePrefix = `inst:${section.id}:`;
      for (const row of section.rows) {
        for (const col of row.columns) {
          const f = col.field;
          if (!f || !CHOICE_TYPES.has(f.type)) continue;
          if (f.advisorRepCodeSelector || f.options.length === 0) continue;
          // Language mode only judges fields that hide an option somewhere; everything else
          // was already checked against its version when the application was reopened.
          if (language && !f.options.some((o) => (o.languages?.length ?? 0) > 0)) continue;
          const allowed = new Set(
            f.options.filter((o) => isOptionOffered(o, language ?? null)).map((o) => o.value),
          );

          // The plain key, plus this field's answer in every instance of a repeated section.
          const keys = Object.keys(next).filter(
            (k) => k === f.id || (k.startsWith(instancePrefix) && k.endsWith(`:${f.id}`)),
          );
          for (const key of keys) {
            const value = next[key];
            if (typeof value === 'string') {
              if (!allowed.has(value)) {
                delete next[key];
                cleared.add(f.id);
              }
            } else if (Array.isArray(value)) {
              const kept = (value as unknown[]).filter(
                (v) => typeof v === 'string' && allowed.has(v),
              );
              if (kept.length === value.length) continue;
              cleared.add(f.id);
              if (kept.length === 0) delete next[key];
              else next[key] = kept as FieldValue;
            }
          }
        }
      }
    }
  }

  if (cleared.size === 0) return { values, clearedFieldIds: [] };
  return { values: next, clearedFieldIds: [...cleared] };
}
